import { memo, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FolderOutline, NucleoAllTask } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn, css } from '@lyearn/style';
import { Loader, Radio, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { SearchBar } from '@/components/SearchBar';
import VirtualizedList from '@/components/VirtualizedList';
import useResponse from '@/hooks/useResponse';
import SearchListEmptyState from '@/modules/corporate/components/SearchListEmptyState';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import useActionItems from '@/modules/tasks/features/actionItems/hooks/useActionItems';
import useUpdateActionItem from '@/modules/tasks/features/actionItems/hooks/useUpdateActionItem';

import useFileActions from '../../../hooks/useFileActions';
import { DialogProps } from '../types';

const dialogSize = css`
  max-width: 52rem;
  height: 72rem;
`;

function ConvertTaskToSubtask({ onClose, task: _task }: DialogProps) {
  const task = _task as ActionItemFragment;

  const { t } = useTranslation();
  const { state } = useFileActions({ onSuccess: onClose });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  const {
    fetching,
    actionItems: _actionItems,
    fetchMore,
    isFetchingMore,
    totalCount,
  } = useActionItems({
    filters: { title: search },
  });

  const actionItems = useMemo(
    () => _actionItems?.filter((item) => item.id !== task.id),
    [_actionItems, task.id],
  );

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const { updateActionItem } = useUpdateActionItem();
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const onSubmit = useCallback(async () => {
    if (!selectedTaskId || !task || !('id' in task)) return;
    setIsSubmitting(true);

    const response = await updateActionItem({
      data: {
        actionItemId: task.id,
        parentId: selectedTaskId,
      },
    });

    const parentTaskName =
      actionItems?.find((item) => item.id === selectedTaskId)?.title || 'the selected task';

    handleResponse({
      condition: !response.error,
      onSuccess: () => {
        showSnackbar({
          message: t('Task:TaskWasMovedTo', { name: parentTaskName }),
        });
        onClose();
      },
    });

    setIsSubmitting(false);
  }, [
    handleResponse,
    onClose,
    actionItems,
    selectedTaskId,
    showSnackbar,
    t,
    task,
    updateActionItem,
  ]);

  return (
    <Modal
      action={{
        disabled: isSubmitting || state.isFetching || fetching || !selectedTaskId,
        isSubmitting: isSubmitting || state.isFetching || fetching,
        text: t('Common:Actions.Convert'),
        onSubmit: onSubmit,
      }}
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      header={{
        title: t('Task:ConvertToSubtask'),
        description: t('Task:SelectParentTask'),
      }}
      onClose={onClose}>
      <Stack className="mx-24 flex-1" direction="col">
        <SearchBar className="grow-0 shrink-0 mb-24" value={search} onChange={setSearch} />
        {!totalCount ? (
          <SearchListEmptyState
            Icon={FolderOutline}
            className="!my-[12rem]"
            search={search}
            title={t('Task:NoTasksAvailable')}
          />
        ) : (
          <>
            <Typography className="mb-12 mx-8" variant="label-01">
              {t('Task:AllTasks')}
            </Typography>
            <VirtualizedList
              ListFooterComponent={isFetchingMore ? <Loader className="mx-auto" size={24} /> : null}
              className="flex-1 border border-border rounded-12 overflow-clip"
              data={actionItems}
              endReached={fetchMore}
              itemContent={(index, actionItem) => (
                <Stack
                  key={actionItem.id}
                  alignItems="center"
                  className={cn(
                    'border-divider ps-16 pe-20 py-10 hover:bg-bg-secondary cursor-pointer',
                    { 'border-t': index > 0 },
                  )}
                  direction="row"
                  gap={8}
                  justifyContent="between"
                  role="button"
                  onClick={() => setSelectedTaskId(actionItem.id)}>
                  <NucleoAllTask className="shrink-0 h-22 w-22" fillColorToken="icon-primary" />
                  <TypographyWithTooltip
                    className="flex-1 truncate"
                    color="text-text-paragraph"
                    numberOfLines={1}
                    variant="body-short-01">
                    {actionItem.title}
                  </TypographyWithTooltip>
                  <Radio
                    checked={selectedTaskId === actionItem.id}
                    onChange={() => setSelectedTaskId(actionItem.id)}
                  />
                </Stack>
              )}
              totalCount={totalCount}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default memo(ConvertTaskToSubtask);
